import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import CustomTable from 'components/Tables/CustomTable';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import ApiService from 'utils/ApiCalls';
import {
  ProcessRow,
  ApiProcessItem,
  DropdownItem,
  ErrorItem,
  ApiError
} from 'store/ducks/InternalControls/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { getProcessColumns } from 'constants/processColumns';
import { internalControlsOp } from 'store/ducks/InternalControls';
import useDeleteWithDependencyCheck from 'utils/hooks/useDeleteWithDependencyCheck';
import * as Styled from '../RiskControl.styled';

import Helpers from 'utils/Helpers';

const ProcessesL2: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { scheme } = useSelector((state: RootState) => state.global);
  const { drawTable, selectedEntityId, lang } = useSelector(
    (state: RootState) => ({
      drawTable: state.internalControls.drawTable,
      selectedEntityId: state.internalControls.selectedEntityId,
      lang: state.global.lang
    })
  );

  // Sync language with i18n
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);
  const [processes, setProcesses] = useState<ProcessRow[]>([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const originalProcessesRef = useRef<ProcessRow[]>([]);
  const [newProcessIds, setNewProcessIds] = useState<Set<number>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const dispatch = useDispatch();

  const { handleDelete: handleProcessDelete } = useDeleteWithDependencyCheck({
    tableName: 'business_process_l2',
    softDeleteFn: (id) => ApiService.softDeleteBusinessProcessL2(Number(id)),
    onSuccess: (id) => setProcesses((prev) => prev.filter((p) => p.id !== id)),
    entityLabel: 'process'
  });

  type SearchKey =
    | 'title'
    | 'description'
    | 'activation'
    | 'type'
    | 'responsible'
    | 'version'
    | 'comments'
    | 'owner';
  type SearchState = { search: string; showSearch: boolean };

  const [searchStates, setSearchStates] = useState<
    Record<SearchKey, SearchState>
  >({
    title: { search: '', showSearch: false },
    description: { search: '', showSearch: false },
    activation: { search: '', showSearch: false },
    type: { search: '', showSearch: false },
    responsible: { search: '', showSearch: false },
    version: { search: '', showSearch: false },
    comments: { search: '', showSearch: false },
    owner: { search: '', showSearch: false }
  });

  const trackingSetProcesses = useCallback(
    (newProcesses: ProcessRow[]) => {
      setProcesses(newProcesses);
      setIsDirty(true);
      dispatch(internalControlsOp.setHasChanges(true));
    },
    [dispatch]
  );

  useEffect(() => {
    setSaveDisabled(!isDirty && newProcessIds.size === 0);
    dispatch(
      internalControlsOp.setHasChanges(isDirty || newProcessIds.size > 0)
    );
  }, [isDirty, newProcessIds, dispatch]);

  const [processType, setProcessType] = useState<
    { label: string; value: number }[]
  >([]);
  const [business_process_l1, setBusinessProcessL1] = useState<
    { label: string; value: number }[]
  >([]);
  const [process_criticality, setProcessCriticality] = useState<
    { label: string; value: number }[]
  >([]);

  useEffect(() => {
    if (!drawTable) return;

    const fetchProcesses = async () => {
      try {
        const response = await ApiService.getBusinessProcessL2BPR(
          selectedEntityId
        );
        const apiRisks: ApiProcessItem[] = response.data.data;
        const mappedProcesses = apiRisks.map((item: ApiProcessItem) => ({
          key: item.id.toString(),
          id: item.id,
          title: item.title,
          description: item.description,
          description1: item.description1,
          description2: item.description2,
          process_image: item.process_image,
          process_risk: item.process_risk,
          responsible_person: item.responsible_person,
          start_date: Helpers.normalizeDate(item.start_date) || '',
          end_date: Helpers.normalizeDate(item.end_date) || '',
          activation_date: Helpers.normalizeDate(item.activation_date) || '',
          active: item.active,
          deleted: item.deleted,
          version: item.version,
          comments: item.comments,
          owner_maintainer: item.owner_maintainer,
          created_at: item.created_at,
          updated_at: item.updated_at,

          process_type: {
            id: item.process_type_id ?? null,
            name: item.process_type_name ?? null
          },

          business_process_l1: {
            id: item.business_process_l1_id ?? null,
            name: item.business_process_l1_name ?? null
          },

          process_criticality: {
            id: item.process_criticality_id ?? null,
            name: item.process_criticality_name ?? null
          }
        }));

        setProcesses(mappedProcesses);
        originalProcessesRef.current = JSON.parse(
          JSON.stringify(mappedProcesses)
        );
        setIsDirty(false);

        dispatch(internalControlsOp.setDrawTable(false));
      } catch (error) {
        console.error('Error fetching processes:', error);
      }
    };

    fetchProcesses();
  }, [drawTable, dispatch, selectedEntityId]);

  useEffect(() => {
    const fetchAllDropdowns = async () => {
      try {
        const [processTypeRes, businessprocessL1Res, processCriticalityRes] =
          await Promise.all([
            ApiService.getPorcessTypeBPR(),
            ApiService.getBusinessProcessL1BPR(selectedEntityId),
            ApiService.getProcessCriticalitiesBPR()
          ]);

        setProcessType(
          [...processTypeRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((type: DropdownItem) => ({
              label: type.name,
              value: type.id
            }))
        );

        setBusinessProcessL1(
          [...businessprocessL1Res.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              (a.title || '').localeCompare(b.title || '')
            )
            .map((type: DropdownItem) => ({
              label: type.title || type.name,
              value: type.id
            }))
        );

        setProcessCriticality(
          processCriticalityRes.data.data.map((type: DropdownItem) => ({
            label: type.name,
            value: type.id
          }))
        );
      } catch (err) {
        console.error('Failed to fetch dropdown data', err);
      }
    };

    fetchAllDropdowns();
  }, [selectedEntityId]);

  const risksColumns = useMemo(
    () =>
      getProcessColumns(
        process_criticality,
        processes,
        processType,
        [],
        [],
        [],
        [],
        [],
        business_process_l1,
        trackingSetProcesses,
        true,
        false,
        'business_process_l1',
        t ? t('BUSINESS_PROCESS_LEVEL_1') : 'Business Process Level 1',
        'L2',
        newProcessIds,
        {
          title: {
            search: searchStates.title.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                title: { ...prev.title, search: val }
              })),
            showSearch: searchStates.title.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                title: { ...prev.title, showSearch: val }
              }))
          },
          description: {
            search: searchStates.description.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                description: { ...prev.description, search: val }
              })),
            showSearch: searchStates.description.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                description: { ...prev.description, showSearch: val }
              }))
          },
          activation: {
            search: searchStates.activation.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                activation: { ...prev.activation, search: val }
              })),
            showSearch: searchStates.activation.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                activation: { ...prev.activation, showSearch: val }
              }))
          },
          type: {
            search: searchStates.type.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                type: { ...prev.type, search: val }
              })),
            showSearch: searchStates.type.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                type: { ...prev.type, showSearch: val }
              }))
          },
          responsible: {
            search: searchStates.responsible.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                responsible: { ...prev.responsible, search: val }
              })),
            showSearch: searchStates.responsible.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                responsible: { ...prev.responsible, showSearch: val }
              }))
          },
          version: {
            search: searchStates.version.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                version: { ...prev.version, search: val }
              })),
            showSearch: searchStates.version.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                version: { ...prev.version, showSearch: val }
              }))
          },
          comments: {
            search: searchStates.comments.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                comments: { ...prev.comments, search: val }
              })),
            showSearch: searchStates.comments.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                comments: { ...prev.comments, showSearch: val }
              }))
          },
          owner: {
            search: searchStates.owner.search,
            setSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                owner: { ...prev.owner, search: val }
              })),
            showSearch: searchStates.owner.showSearch,
            setShowSearch: (val) =>
              setSearchStates((prev) => ({
                ...prev,
                owner: { ...prev.owner, showSearch: val }
              }))
          }
        },
        t,
        handleProcessDelete
      ),
    [
      processes,
      processType,
      business_process_l1,
      process_criticality,
      newProcessIds,
      trackingSetProcesses,
      searchStates,
      t
    ]
  );

  const FILTER_CONFIG: Record<SearchKey, keyof ProcessRow> = {
    title: 'title',
    description: 'description',
    activation: 'activation_date',
    type: 'process_type',
    responsible: 'responsible_person',
    version: 'version',
    comments: 'comments',
    owner: 'owner_maintainer'
  };

  const getFilteredProcesses = () => {
    return processes.filter((p) => {
      return (Object.keys(FILTER_CONFIG) as SearchKey[]).every((key) => {
        const searchValue = searchStates[key].search.trim();
        if (!searchValue) return true;
        const field = FILTER_CONFIG[key];
        const value = p[field];
        if (key === 'type') {
          return (value as ProcessRow['process_type'])?.name
            ?.toLowerCase()
            .includes(searchValue.toLowerCase());
        }
        return (value as string)
          ?.toLowerCase()
          .includes(searchValue.toLowerCase());
      });
    });
  };

  const filteredProcesses = getFilteredProcesses();

  const handleAddProcessRow = () => {
    const newId = generateUniqueId();
    const newRow: ProcessRow = {
      id: newId,
      key: (processes.length + 1).toString(),
      title: '',
      description: '',
      description1: '',
      description2: '',
      process_image: '',
      process_risk: '',
      responsible_person: '',
      start_date: '',
      end_date: '9999-01-01',
      activation_date: '',
      active: '',
      deleted: '',
      version: '',
      comments: '',
      owner_maintainer: '',
      created_at: '',
      updated_at: '',
      process_type: { id: null, name: null },
      business_process_l1: { id: null, name: null },
      business_process_l3: { id: null, name: null },
      process_criticality: { id: null, name: null }
    };

    trackingSetProcesses([...processes, newRow]);
    setNewProcessIds((prev) => new Set([...prev, newId]));
  };

  const preparePayload = (processesPerRow: ProcessRow[]) => {
    return {
      data_list: processesPerRow.map((process) => ({
        id: process.id,
        title: process.title || null,
        description: process.description || null,
        process_image: process.process_image || null,
        entity_id: selectedEntityId,
        process_type_id: process.process_type?.id ?? null,
        business_process_l1_id: process.business_process_l1?.id ?? null,
        process_criticality_id: process.process_criticality?.id ?? null,
        responsible_person: process.responsible_person || null,
        version: process.version === '' ? null : process.version,
        comments: process.comments || null,
        owner_maintainer: process.owner_maintainer || null,
        start_date: process.start_date || null,
        end_date: process.end_date || '9999-01-01',
        activation_date: process.activation_date || null
      }))
    };
  };

  const handleProcessSave = async () => {
    if (!isDirty && newProcessIds.size === 0) {
      notification.info({
        message: 'No Changes',
        description: 'No changes detected to save.',
        placement: 'topRight',
        duration: 3
      });
      return;
    }

    // Find changed processes by comparing with original
    const changedProcesses = processes.filter((process) => {
      if (newProcessIds.has(process.id)) return true;
      const original = originalProcessesRef.current.find(
        (p) => p.id === process.id
      );
      if (!original) return false;

      // Check specific fields that might change
      const fieldsToCheck: (keyof ProcessRow)[] = [
        'title',
        'description',
        'responsible_person',
        'start_date',
        'end_date',
        'activation_date',
        'version',
        'comments',
        'owner_maintainer'
      ];
      let hasChanges = false;

      fieldsToCheck.forEach((field) => {
        if (process[field] !== original[field]) {
          hasChanges = true;
        }
      });

      // Check nested objects
      if (
        JSON.stringify(process.process_type) !==
        JSON.stringify(original.process_type)
      ) {
        hasChanges = true;
      }

      if (
        JSON.stringify(process.business_process_l1) !==
        JSON.stringify(original.business_process_l1)
      ) {
        hasChanges = true;
      }

      if (
        JSON.stringify(process.process_criticality) !==
        JSON.stringify(original.process_criticality)
      ) {
        hasChanges = true;
      }

      return hasChanges;
    });

    if (changedProcesses.length === 0) {
      notification.info({
        message: 'No Changes',
        description: 'No changes detected to save.',
        placement: 'topRight',
        duration: 3
      });
      return;
    }

    const payload = preparePayload(changedProcesses);

    try {
      const response = await ApiService.saveProcessL2BPR(payload);
      originalProcessesRef.current = JSON.parse(JSON.stringify(processes));
      setNewProcessIds(new Set());
      setIsDirty(false);
      dispatch(internalControlsOp.setHasChanges(false));
      notification.success({
        message: 'Processes Saved',
        description:
          response?.data?.message ||
          `${changedProcesses.length} process(es) saved successfully.`,
        placement: 'topRight',
        duration: 3
      });
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const errorMessages =
        apiError?.errors?.map(
          (e: ErrorItem) => `Row ${e.index + 1}: ${e.error}`
        ) || [];
      const description =
        errorMessages.length > 0
          ? errorMessages.join('\n')
          : 'An error occurred while saving.';

      notification.error({
        message: 'Save Error',
        description,
        placement: 'topRight',
        duration: 20
      });
    }
  };

  return (
    <>
      <Styled.GlobalTooltipStyles />
      <InternalControlLayout onSave={handleProcessSave}>
        <Styled.ContentWrapper>
          <CustomTable
            drawTable={true}
            columns={risksColumns}
            dataSource={filteredProcesses}
            itemsCount={filteredProcesses.length}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredProcesses.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page: number, size: number) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                }
              }
            }}
            scroll={{ x: 2500 }}
          />
          <Styled.AddRowArea>
            <Button type='primary' onClick={handleAddProcessRow}>
              Add new level 2 process
            </Button>
          </Styled.AddRowArea>
        </Styled.ContentWrapper>
      </InternalControlLayout>
    </>
  );
};

export default ProcessesL2;
